import type { FootprintId } from "../data/moduleFootprints.js";
import type { ModuleDefinition, ModuleRarity } from "../modules/ProjectileModule.js";

/**
 * 융합(ModuleFusion) 컴포넌트 — 융합 산출물이 합쳐 가진 원본 모듈 1개의 전투 해석 정보.
 * 전투(CombatSystem.buildSpec)는 융합 인스턴스의 fused 배열을 돌며 각 컴포넌트의 (definitionId, rarity)로
 * DefinitionModule을 만들어 effect를 **모두 적용**한다 — "두 모듈 능력을 그대로 합친" 결과(사용자 명시).
 * ⚠ level은 컴포넌트에 저장하지 않는다 — 레벨은 플레이어 자산(PlayerModuleLevelController)이라
 * 전투 시 각 컴포넌트의 definitionId로 player level을 조회한다(2축 분리).
 */
export interface FusedComponent {
  readonly definitionId: string;
  readonly rarity: ModuleRarity;
}

/**
 * ModuleInstance — 플레이어가 실제로 가지고 있는 보유 모듈 개체 (지시문 §2·§9).
 * 같은 ModuleDefinition을 여러 개 보유할 수 있으며, 그리드에 배치되는 것은 definitionId가 아니라 instanceId다.
 * acquiredAt/instanceId는 순수성 위해 주입받는다(Date.now/random 직접 호출 금지).
 * shapeId/footprintId는 획득 시 정의 조각 풀(def.pieces)에서 pickPiece로 뽑은 한 조각(연결 모양 + 점유 칸) — 불변.
 * rarity는 합성(ModuleMerge)으로 상승한 **등급 오버라이드** — 미지정 시 정의(ModuleDefinition) 등급을 따른다.
 * 등급 조회는 항상 effectiveRarity()를 거쳐 인스턴스 오버라이드 → 정의 → "common" 순으로 폴백한다.
 * ⚠ **레벨은 인스턴스에 저장하지 않는다** — 레벨은 moduleId별 플레이어 영구 자산(PlayerModuleLevelController).
 *   같은 definitionId의 모든 인스턴스가 같은 player level을 공유하며 환생해도 유지된다(등급은 인스턴스라 소멸).
 */
export interface ModuleInstance {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly acquiredAt: number;
  locked: boolean;
  readonly footprintId: FootprintId;
  /** 조각의 연결 모양 id (SHAPE_CATALOG). footprintId와 함께 획득 시 pickPiece로 정해져 불변 — 배치·렌더·경로 포트 합성에 사용. */
  readonly shapeId: string;
  /** 합성으로 상승한 등급 오버라이드 (없으면 정의 등급). 전투 배율·표시색·dim 기준의 SSoT는 effectiveRarity(). */
  readonly rarity?: ModuleRarity;
  /**
   * 융합(ModuleFusion) 산출물이면 합쳐진 컴포넌트들(2개) — 전투 시 각 컴포넌트 effect를 모두 적용한다.
   * **존재 자체가 "융합 산출물 = 합성·융합 재료에서 제외" 신호**(canMerge/canFuse가 배제). 미지정 = 일반 모듈.
   * top-level definitionId/footprintId = 표시·그리드용(첫 컴포넌트·공유 footprint), rarity = 컴포넌트 최고 등급(표시색).
   */
  readonly fused?: readonly FusedComponent[];
}

/** instanceId 생성기 — 주입으로 테스트 결정론 확보. */
export type InstanceIdSource = () => string;

/** 새 ModuleInstance 생성 (순수). id/시간은 주입받는다. shapeId/footprintId 미지정 시 straight_h/single. */
export function createModuleInstance(args: {
  definitionId: string;
  instanceId: string;
  acquiredAt?: number;
  locked?: boolean;
  footprintId?: FootprintId;
  shapeId?: string;
  rarity?: ModuleRarity;
  fused?: readonly FusedComponent[];
}): ModuleInstance {
  return {
    instanceId: args.instanceId,
    definitionId: args.definitionId,
    acquiredAt: args.acquiredAt ?? 0,
    locked: args.locked ?? false,
    footprintId: args.footprintId ?? "single",
    shapeId: args.shapeId ?? "straight_h",
    // 미지정이면 키 자체를 생략 — 기존(오버라이드 없는) 인스턴스와 직렬화 형태 동일.
    ...(args.rarity ? { rarity: args.rarity } : {}),
    // 융합 산출물만 fused 키 보유 — 일반 모듈은 키 생략(직렬화 형태 동일 + 제외 신호).
    ...(args.fused && args.fused.length > 0 ? { fused: args.fused } : {}),
  };
}

/**
 * 인스턴스의 유효 등급 — 합성 오버라이드(inst.rarity) → 정의 등급(def.rarity) → "common" 폴백.
 * 전투 등급 배율(DefinitionModule)·표시색(RARITY_COLOR)·합성 dim 기준이 모두 이 함수를 거쳐 일관된다.
 */
export function effectiveRarity(
  inst: Pick<ModuleInstance, "rarity">,
  def: Pick<ModuleDefinition, "rarity"> | undefined,
): ModuleRarity {
  return inst.rarity ?? def?.rarity ?? "common";
}

/**
 * 결정론 카운터 기반 instanceId 생성기. prefix로 세션 간/재시작 후 충돌을 회피한다
 * (예: `createCounterIdSource("m" + Date.now() + "-")`). 테스트는 고정 prefix로 결정론 확보.
 */
export function createCounterIdSource(prefix = "inst-", start = 0): InstanceIdSource {
  let n = start;
  return () => `${prefix}${++n}`;
}
